import { Place_Source } from "./Place";
import Common, { Settings, SortByEnum, COLORS } from "../Common/Common";
import { DateTime } from "luxon";

export default class Appointment {
  constructor(schedule) {
    this.Subject = "New";
    this.Body = "";
    this.Start = "";
    this.End = "";
    this.Places = "";
    this.ID = "";
    this.Source = Place_Source.USER;
    this.Itinerary = "Main";
    // Set by TripAI.js's parseSched from the AI's own IsTravel label —
    // Schedule.js's recalculateSchedule treats a travel-leg event's Start as
    // a fixed real-world departure time it must never reposition, unlike
    // every other event.
    this.IsTravel = false;
    // Stable identity for this event, unlike .ID above (regenerated randomly
    // on every decode — see json_to_appointment below). Minted once here (or
    // in json_to_appointment, if missing from decoded JSON) and preserved
    // verbatim after that — used by CommentsSection.js so a comment keeps
    // pointing at the same event across reloads. copy() below mints a fresh
    // one instead of inheriting, since a copy is a new occurrence.
    this.CommentKey = "";
    if (schedule != null) {
      var r = Math.floor(Math.random() * 1000000000 + 1);
      while (schedule.get_event(r) != null) r = Math.floor(Math.random() * 1000000000 + 1);
      this.ID = r.toString();
      this.CommentKey = `${DateTime.utc().toISO()}-${Math.random().toString(36).slice(2, 10)}`;
    }
  }

  createHTML(placesObj) {
    let isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    let font_size = isMobile ? "100%" : "100%";
    let event = this;
    let html_doc = "";
    const linkScript = `<script>
      window.customLink = function(url) { if (window.parent && window.parent.customLink) window.parent.customLink(url); };
      document.addEventListener('click', function(e) {
        var a = e.target.closest('a[href]');
        if (!a) return;
        var href = a.getAttribute('href');
        if (!href || href.startsWith('http://') || href.startsWith('https://')) return;
        e.preventDefault();
        window.customLink(href);
      });
    <\/script>`;
    const headStyles = `<style type="text/css">.c0 { font-family: 'Arial'; text-decoration: none } .c1 { margin: 0px 0px 10px; padding: 10px 0px; font-size: 100%; text-decoration: none } .c2 { font-weight: bold; text-decoration: none } .c3 { text-decoration: none } .c4 { color: Blue; text-decoration: none } .c5 { margin: 0px 0px 10px; text-decoration: none } </style>`;
    html_doc = `<html><head>${headStyles}${linkScript}</head><body class="c0">`;
    let startDate = DateTime.fromISO(event.Start, { zone: "utc" });
    let endDate = DateTime.fromISO(event.End, { zone: "utc" });
    html_doc += `<div style="font-size: ${font_size};">`;
    html_doc += `<p class="c1"><span class="c2">${Common.formatDateLuxon(startDate)} </span></p>`;
    html_doc += `<p class="c1"><span class="c2">${Common.formatTimeLuxon(startDate)}-${Common.formatTimeLuxon(
      endDate
    )}<br/></span>`;
    html_doc += `<span class="c1">${event.Subject}<br/></span>`;
    html_doc += `<span class="c3">${event.Body}<br/></span>`;
    const places = event.get_places(placesObj);
    //console.log("places", places);
    for (let j = 0; j < places.length; j++) {
      // Wrapped in its own block — raw Info (e.g. a Wikipedia enrichment
      // blurb ending bare at "...See more on Wikipedia</a>", see
      // placeEnrichment.js) was concatenated directly against the NEXT
      // place's Info with no separator, and then against the Name-links
      // loop below — an event with multiple places attached ran them all
      // together with no space when the text was selected/copied. Same
      // fix as Guide.js's createHTMLItinerary. Found 2026-10-05.
      if (typeof places[j].Name !== "undefined" && places[j].Info) html_doc += `<div style="margin-bottom:14px;">${places[j].Info}</div>`;
    }
    for (let j = 0; j < places.length; j++) {
      if (typeof places[j].Name !== "undefined")
        html_doc += `<a href="placemenulink:${places[j].ID}" class="c4"><span class="c3">${places[j].Name}${
          j === places.length - 1 ? "" : "; "
        } </span></a>`;
    }
    html_doc += `</p></body></div></html>`;
    return html_doc;
  }

  json_to_appointment(schedule, event_json, old) {
    this.Subject = event_json.Subject;
    this.Body = event_json.Body;
    if (old) {
      //console.log("OLD event_json.Start:", event_json.Start);
      this.Start = DateTime.fromISO(Common.LuxonDateTimeFromJSDate(new Date(event_json.Start)), { zone: "utc" });
      //console.log("OLD this.Start:", this.Start);
      this.End = DateTime.fromISO(Common.LuxonDateTimeFromJSDate(new Date(event_json.End)), { zone: "utc" });
    } else {
      //console.log("NEW event_json.Start:", event_json.Start);
      this.Start = DateTime.fromISO(event_json.Start, { zone: "utc" });
      //console.log("NEW this.Start:", this.Start);
      this.End = DateTime.fromISO(event_json.End, { zone: "utc" });
    }
    //console.log("event_json.Start", event_json.Start, this.Start, event_json.Itinerary, event_json.Subject);
    //console.log("event_json.Start_1", this.Start, event_json.Itinerary, event_json.Subject);

    this.Places = event_json.Places;
    if (this.Places === null || typeof this.Places === "undefined") this.Places = "";
    this.ID = "";
    if (typeof event_json.Itinerary != "undefined") this.Itinerary = event_json.Itinerary;
    if (typeof event_json.IsTravel != "undefined") this.IsTravel = event_json.IsTravel;
    this.Source = Place_Source.GUIDE;
    // Unlike .ID above, preserve an existing CommentKey rather than always
    // minting a fresh one — that's the whole point of this field. A guide
    // saved before this field existed has none yet; this self-heals it on
    // first decode (same pattern as guide_data's guide_info_id/user_id
    // backfill), and from the next save onward it's stable for good.
    if (typeof event_json.CommentKey === "string" && event_json.CommentKey !== "") {
      this.CommentKey = event_json.CommentKey;
    } else {
      this.CommentKey = `${DateTime.utc().toISO()}-${Math.random().toString(36).slice(2, 10)}`;
    }
    if (schedule != null) {
      var r = Math.floor(Math.random() * 1000000000 + 1);
      while (schedule.get_event(r) != null) r = Math.floor(Math.random() * 1000000000 + 1);
      this.ID = r.toString();
    }
  }
  copy(cevent) {
    this.Subject = cevent.Subject;
    this.Body = cevent.Body;
    this.Start = cevent.Start; //Common.dateToString(cevent.Start);
    this.End = cevent.End; //Common.dateToString(cevent.End);
    this.Places = cevent.Places;
    //this.ID = cevent.ID;
    // A copy is a new occurrence, not the same event — it gets its own fresh
    // comment thread rather than inheriting cevent's.
    this.CommentKey = `${DateTime.utc().toISO()}-${Math.random().toString(36).slice(2, 10)}`;
    this.Source = Place_Source.USER;
    if (typeof cevent.Itinerary != "undefined") this.Itinerary = cevent.Itinerary;
    if (typeof cevent.IsTravel != "undefined") this.IsTravel = cevent.IsTravel;
  }
  get_places(places_obj) {
    var placesList = [];
    //console.log("get_place_ids1");
    if (this.Places == null) return placesList;
    const placesStr = String(this.Places).trim();
    if (!placesStr) return placesList;
    // this.Places is a comma-separated ID string that can end up with the
    // same ID twice (e.g. a place added to an event more than once) — every
    // caller (itinerary building included) just renders whatever this
    // returns, so a duplicate ID here meant a place's Notes/photo got
    // printed twice within the SAME event block. De-duping centrally here
    // fixes it for every consumer at once. Fixed 2026-08-29 — see memory:
    // project_cost_revenue_viability.
    const seenIds = new Set();
    if (placesStr.indexOf(",") != -1) {
      var places = placesStr.split(",");
      for (var key in places) {
        const id = places[key].trim();
        if (seenIds.has(id)) continue;
        var Place = places_obj.getPlace(id);
        if (Place != null) {
          seenIds.add(id);
          placesList.push(Place);
        }
      }
    } else {
      var Place = places_obj.getPlace(placesStr);
      if (Place != null) {
        placesList.push(Place);
      }
    }
    return placesList;
  }
  // get_places_old() {
  //   var placeList = [];
  //   if (this.Places == null) return placeList;
  //   if (this.Places.indexOf(",") != -1) {
  //     var places = this.Places.split(",");
  //     for (var key in places) {
  //       var Place = getPlace(guide_obj.places.all_places, $.trim(places[key]));
  //       if (Place != null) placeList.push(Place);
  //     }
  //   } else {
  //     var Place = getPlace(guide_obj.places.all_places, $.trim(this.Places));
  //     if (Place != null) {
  //       placeList.push(Place);
  //     }
  //   }
  //   return placeList;
  // }
  // get_placeIDs() {
  //   var placeList = [];
  //   if (this.Places == null) return placeList;
  //   if (this.Places.indexOf(",") != -1) {
  //     var places = this.Places.split(",");
  //     for (var key in places) {
  //       var Place = getPlace(guide_obj.places.all_places, $.trim(places[key]));
  //       if (Place != null) placeList.push(Place.ID);
  //     }
  //   } else {
  //     var Place = getPlace(guide_obj.places.all_places, $.trim(this.Places));
  //     if (Place != null) {
  //       placeList.push(Place.ID);
  //     }
  //   }
  //   return placeList;
  // }
}
